/**
 * Infer content kind + embed URL from any URL a human might paste.
 * Single source of truth for Loom / YouTube / Vimeo / Canva / Google Slides /
 * Google Docs / PDF detection. Used by Library + Onboarding lessons.
 *
 * Returns:
 *   kind:      "video" | "deck" | "doc" | "pdf" | "unknown"
 *   embedUrl:  string  — the URL you can drop into an <iframe> (or <video>)
 *   openUrl:   string  — the original/canonical URL for "open in new tab"
 *   iconHint:  "play" | "deck" | "doc" | "link"
 *   label:     string  — short human name like "Loom", "Canva", "Google Slides"
 */
export function inferContent(raw) {
  const url = String(raw || "").trim();
  if (!url) return { kind: "unknown", embedUrl: null, openUrl: url, iconHint: "link", label: "Link" };

  // ── HTML blob handling: extract a usable URL if user pasted an embed snippet
  //    or GIF thumbnail. Matches the first src="..." or href="..." we recognise.
  if (/<\s*(iframe|a|img)\b/i.test(url)) {
    const urls = Array.from(url.matchAll(/(?:src|href)\s*=\s*["']([^"']+)["']/gi)).map((m) => m[1]);
    for (const u of urls) {
      // Recurse on the first URL that isn't an image/gif asset URL
      if (!/\.(gif|png|jpe?g|webp)(\?|#|$)/i.test(u)) return inferContent(u);
    }
    // Fall through to treat any matched URL (even GIFs) as link-only
    if (urls.length) return inferContent(urls[0]);
  }

  // ── Our own served library files ──
  if (/\/api\/pitch-decks\/file\//i.test(url)) {
    const ext = (url.split(".").pop() || "").toLowerCase().split("?")[0];
    if (ext === "pdf") return { kind: "pdf", embedUrl: url, openUrl: url, iconHint: "doc", label: "PDF" };
    if (["ppt", "pptx"].includes(ext)) return { kind: "deck", embedUrl: null, openUrl: url, iconHint: "deck", label: "PowerPoint" };
    if (["doc", "docx"].includes(ext)) return { kind: "doc", embedUrl: null, openUrl: url, iconHint: "doc", label: "Word" };
    if (["xls", "xlsx", "csv"].includes(ext)) return { kind: "doc", embedUrl: null, openUrl: url, iconHint: "doc", label: "Spreadsheet" };
    if (["png", "jpg", "jpeg", "webp"].includes(ext)) return { kind: "doc", embedUrl: url, openUrl: url, iconHint: "doc", label: "Image" };
    if (["txt", "md"].includes(ext)) return { kind: "doc", embedUrl: url, openUrl: url, iconHint: "doc", label: "Text" };
    return { kind: "doc", embedUrl: null, openUrl: url, iconHint: "doc", label: "File" };
  }

  // ── Video: Loom (handles share URL, embed URL, and CDN thumbnail GIFs) ──
  const loomMatch = url.match(/loom\.com\/(?:share|embed|v)\/([a-f0-9]{12,})/i)
    || url.match(/cdn\.loom\.com\/sessions\/thumbnails\/([a-f0-9]{12,})/i);
  if (loomMatch) {
    const id = loomMatch[1];
    return {
      kind: "video",
      embedUrl: `https://www.loom.com/embed/${id}`,
      openUrl: `https://www.loom.com/share/${id}`,
      iconHint: "play",
      label: "Loom",
    };
  }

  // ── Video: YouTube ──
  const ytWatch = url.match(/youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{6,})/i);
  const ytShort = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/i);
  const ytEmbed = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/i);
  const ytId = (ytWatch && ytWatch[1]) || (ytShort && ytShort[1]) || (ytEmbed && ytEmbed[1]);
  if (ytId) {
    return {
      kind: "video",
      embedUrl: `https://www.youtube.com/embed/${ytId}`,
      openUrl: url,
      iconHint: "play",
      label: "YouTube",
    };
  }

  // ── Video: Vimeo ──
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) {
    return {
      kind: "video",
      embedUrl: `https://player.vimeo.com/video/${vimeo[1]}`,
      openUrl: url,
      iconHint: "play",
      label: "Vimeo",
    };
  }

  // ── Deck: Canva ──
  if (/canva\.com\/design\//i.test(url)) {
    // Canva share URL variants: /view, /edit — embed via ?embed
    const base = url.split("?")[0].replace(/\/(edit|view).*$/, "/view");
    return {
      kind: "deck",
      embedUrl: `${base}?embed`,
      openUrl: url,
      iconHint: "deck",
      label: "Canva",
    };
  }

  // ── Deck: Google Slides ──
  if (/docs\.google\.com\/presentation\//i.test(url)) {
    // Convert /edit → /embed for a clean presentation embed
    const base = url.split("?")[0].replace(/\/(edit|pub|preview).*$/, "");
    return {
      kind: "deck",
      embedUrl: `${base}/embed?start=false&loop=false&delayms=5000`,
      openUrl: url,
      iconHint: "deck",
      label: "Google Slides",
    };
  }

  // ── Doc: Google Docs ──
  if (/docs\.google\.com\/document\//i.test(url)) {
    const base = url.split("?")[0].replace(/\/(edit|pub|preview).*$/, "");
    return {
      kind: "doc",
      embedUrl: `${base}/preview`,
      openUrl: url,
      iconHint: "doc",
      label: "Google Doc",
    };
  }

  // ── Doc: Google Sheets ──
  if (/docs\.google\.com\/spreadsheets\//i.test(url)) {
    const base = url.split("?")[0].replace(/\/(edit|pub|preview).*$/, "");
    return {
      kind: "doc",
      embedUrl: `${base}/preview`,
      openUrl: url,
      iconHint: "doc",
      label: "Google Sheet",
    };
  }

  // ── Doc: Notion / Dropbox Paper (link only; no reliable iframe) ──
  if (/(notion\.site|notion\.so|paper\.dropbox\.com)/i.test(url)) {
    return {
      kind: "doc",
      embedUrl: null,
      openUrl: url,
      iconHint: "doc",
      label: url.includes("notion") ? "Notion" : "Dropbox Paper",
    };
  }

  // ── PDF: direct link ──
  if (/\.pdf(\?|#|$)/i.test(url)) {
    return {
      kind: "pdf",
      embedUrl: url,
      openUrl: url,
      iconHint: "doc",
      label: "PDF",
    };
  }

  // ── Figma ──
  if (/figma\.com\/(file|proto|design)\//i.test(url)) {
    return {
      kind: "deck",
      embedUrl: `https://www.figma.com/embed?embed_host=dentsuamp&url=${encodeURIComponent(url)}`,
      openUrl: url,
      iconHint: "deck",
      label: "Figma",
    };
  }

  // ── Unknown ──
  return { kind: "unknown", embedUrl: null, openUrl: url, iconHint: "link", label: "Link" };
}

/**
 * Coarser grouping for the Library's Watch/Read toggle.
 */
export function kindToBucket(kind) {
  if (kind === "video") return "watch";
  if (kind === "deck" || kind === "doc" || kind === "pdf") return "read";
  return "other";
}
